import { Dialog } from '@angular/cdk/dialog';
import { Injectable } from '@angular/core';
import { SacraRelay__factory } from '@data/abi';
import { TransactionDataModel } from '@models/transaction-data.model';
import { AppStateService } from '@services/app-state-service/app-state.service';
import { DestroyService } from '@services/destroy.service';
import { ErrorService } from '@services/error.service';
import { FeesExtension, ON_CHAIN_CALL_DELAY, ON_CHAIN_CALL_RETRY } from '@services/onchain/FeesExtension';
import { ProviderService } from '@services/provider.service';
import { StorageService } from '@services/storage.service';
import { DelegateDialogComponent, STORAGE_DELEGATE_HIDE } from '@shared/components/delegate-dialog/delegate-dialog';
import { ADDRESS_ZERO, GET_CORE_ADDRESSES } from '@shared/constants/addresses/addresses.constant';
import { CHECKBOX_STATE } from '@shared/constants/checkbox-states.constant';
import { MAIN_ROUTES } from '@shared/constants/routes.constant';
import { adjustGasLimit } from '@shared/utils';
import { parseUnits } from 'ethers';
import { NGXLogger } from 'ngx-logger';
import { catchError, from, retry, forkJoin, concatMap, switchMap, takeUntil, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RelayService extends FeesExtension {
  delegateDialogOpened = false;

  constructor(
    private providerService: ProviderService,
    private errorService: ErrorService,
    private storageService: StorageService,
    private dialog: Dialog,
    private logger: NGXLogger,
    private appStateService: AppStateService,
  ) {
    super();
  }

  // --- FACTORIES ---

  private createRelay(chainId: number, signer?: string) {
    return SacraRelay__factory.connect(
      GET_CORE_ADDRESSES(chainId).relay,
      this.providerService.getProviderForRead(signer),
    );
  }

  // --- VIEWS ---

  userInfo$(chainId: number, account: string) {
    this.logger.trace('Relay user info', chainId, account);
    return from(this.createRelay(chainId).userInfo(account)).pipe(
      tap(userInfo => {
        this.appStateService.setUserInfo(userInfo);
      }),
      retry({ count: ON_CHAIN_CALL_RETRY, delay: ON_CHAIN_CALL_DELAY }),
      catchError(error => {
        this.appStateService.setUserInfo(null);
        return this.errorService.onCatchError(error);
      }),
    );
  }

  showDelegateDialogIfNecessary(
    force: boolean,
    routerUrl: string,
    destroy$: DestroyService,
    chainId?: number,
    account?: string,
  ) {
    if (
      chainId &&
      account &&
      !this.providerService.delegateDialogOpened &&
      routerUrl !== '/' &&
      routerUrl !== '/' + MAIN_ROUTES.CREATE &&
      routerUrl !== '/' + MAIN_ROUTES.TOP_LIST &&
      routerUrl !== '/' + MAIN_ROUTES.AUTH &&
      routerUrl !== '/' + MAIN_ROUTES.AUTH + '/' + MAIN_ROUTES.WALLET &&
      routerUrl !== '/' + MAIN_ROUTES.DELEGATE
    ) {
      this.userInfo$(chainId ?? -1, account ?? 'USER_INFO_NO_ACC')
        .pipe(takeUntil(destroy$))
        .subscribe(userInfo => {
          if (userInfo.delegator !== ADDRESS_ZERO) {
            this.showDelegateDialog(force);
          }
        });
    }
  }

  showDelegateDialog(force: boolean): boolean {
    if (!force) {
      let isHideDelegate = false;

      if (this.storageService.get(STORAGE_DELEGATE_HIDE)) {
        isHideDelegate = JSON.parse(this.storageService.get(STORAGE_DELEGATE_HIDE)) === CHECKBOX_STATE.CHECKED;
      }

      if (isHideDelegate || this.delegateDialogOpened || this.providerService.getLocalPrivateKey() !== null) {
        return false;
      }
    }
    this.delegateDialogOpened = true;

    this.dialog.open(DelegateDialogComponent, {
      panelClass: 'app-overlay-pane',
      disableClose: true,
      data: {},
    });

    return true;
  }

  // --- CALLS ---

  createDelegatedTx(data: TransactionDataModel, user: string, delegator: string) {
    return forkJoin([from(data.txPopulated), this.userInfo$(data.chainId ?? -1, user)]).pipe(
      switchMap(([txPopulated, userInfo]) => {
        return from(
          this.createRelay(data.chainId ?? -1, delegator).callFromDelegator.populateTransaction({
            chainId: data.chainId ?? -1,
            target: txPopulated.to,
            data: txPopulated.data,
            user: user,
            userNonce: userInfo.nonce,
            userDeadline: Math.floor(new Date().getTime() / 1000) + 60 * 3,
          }),
        );
      }),
    );
  }

  delegate$(chainId: number, delegator: string, amount: number) {
    return from(this.createRelay(chainId).delegate.estimateGas(delegator)).pipe(
      switchMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas => {
        return this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Delegate',
            subgraphWaitUserData: false,
            showLoadingScreen: true,
            txPopulated: this.createRelay(chainId).delegate.populateTransaction(delegator),
            value: parseUnits(amount.toFixed(18)),
            gasLimit: adjustGasLimit(gas),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            isDelegatedRelayPossible: false,
          }),
        );
      }),
      catchError(this.errorService.onCatchError),
    );
  }

  closeDelegation$(chainId: number, user: string, amount: number, delegator: string) {
    console.log('close Delegation', chainId, user, amount, delegator);
    return from(this.createRelay(chainId, delegator).closeDelegation.estimateGas(user)).pipe(
      switchMap(gasEstimation => this.updateCurrentFees$(this.providerService, gasEstimation)),
      concatMap(gas => {
        return this.providerService.onChainCall(
          new TransactionDataModel({
            name: 'Close delegation',
            subgraphWaitUserData: false,
            showLoadingScreen: false,
            txPopulated: this.createRelay(chainId, delegator).closeDelegation.populateTransaction(user),
            value: parseUnits(amount.toFixed(18)),
            gasLimit: adjustGasLimit(gas, 255081n),
            maxFeePerGas: this.maxFeePerGas,
            maxPriorityFeePerGas: this.maxPriorityFeePerGas,
            gasPrice: this.gasPrice,
            useDelegatedPK: true,
            isSponsoredRelayPossible: false,
            isDelegatedRelayPossible: false,
          }),
        );
      }),
      catchError(this.errorService.onCatchError),
    );
  }
}
